import moment from "moment";
import { useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { CarbonCopies, DocumentParticipantMapping, DocumentVerification, ModuSignStartDocumentRequest } from "src/api/contract/modusign-types";
import { BaseButton, BaseCheckbox, BaseSelect } from "src/components";
import BaseChipWithBtn from "src/components/BaseChipWithBtn";
import { BaseDatePicker } from "src/components/BaseDatePicker";
import { BaseInput } from "src/components/BaseInput";
import { useModal } from "src/recoil/modalState/hook";
import { formatPhoneNumber, INVALID_PHONE_NUMBER_MESSAGE, isValidKoreanMobile, validateEmail, YmdFormat } from "src/utils";
import ModuSignDCertVerify from "./components/ModuSignDCertVerify";
import ModuSignExternalReference from "./components/ModuSignExternalReference";
import ModuSignPasswordVerify from "./components/ModuSignPasswordVerify";
import ModuSignPhoneVerify from "./components/ModuSignPhoneVerify";
import { LanguageOptions, SigningMethodOptions, verificationOptions } from "./modusign-constants";

type VerificationType = "mobileIdentification" | "password" | "dCert";

type Props = {
  useFormReturn: UseFormReturn<ModuSignStartDocumentRequest>;
};

const ModuSignStep2 = ({ useFormReturn }: Props) => {
  const {
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useFormReturn;
  const { setAbstractModalZ2 } = useModal();

  const [isPasswordUsed, setIsPasswordUsed] = useState(false);
  const [passwordHidden, setPasswordHidden] = useState(true);

  // 모달 닫을 때 해당 타입의 verification 필드 제거
  const handleVerificationClose = (type: VerificationType, idx: number) => {
    const currentVerification = getValues(`document.participantMappingsList.${idx}.verification`) || {};

    // 해당 타입 필드 제거
    const { [type]: removed, ...leftVerification } = currentVerification;

    if (!watch(`document.participantMappingsList.${idx}.verification.${type}.used`)) {
      // 삭제된 verification으로 업데이트
      setValue(`document.participantMappingsList.${idx}.verification`, leftVerification);
    }

    setAbstractModalZ2({ isOpen: false });
  };

  const openVerificationModal = (type: VerificationType, idx: number) => {
    setAbstractModalZ2({
      isOpen: true,
      size: "medium",
      className: "dialog-modal",
      children: (
        <>
          {/* 휴대폰 인증 */}
          {type === "mobileIdentification" && (
            <ModuSignPhoneVerify
              control={control} //
              verifyIdx={idx}
              onClose={() => handleVerificationClose(type, idx)}
              setValueVerification={(verification: { name: string; phoneNumber: string }) => {
                setValue(`document.participantMappingsList.${idx}.verification.${type}`, { ...verification, used: true });
                setAbstractModalZ2({ isOpen: false });
              }}
            />
          )}
          {/* 비밀번호 인증 */}
          {type === "password" && (
            <ModuSignPasswordVerify
              control={control} //
              verifyIdx={idx}
              onClose={() => handleVerificationClose(type, idx)}
              setValueVerification={(verification: { value: string; hint: string }) => {
                setValue(`document.participantMappingsList.${idx}.verification.${type}`, { ...verification, used: true });
                setAbstractModalZ2({ isOpen: false });
              }}
            />
          )}
          {/* 법인 공동 인증서 */}
          {type === "dCert" && (
            <ModuSignDCertVerify
              control={control} //
              verifyIdx={idx}
              onClose={() => handleVerificationClose(type, idx)}
              setValueVerification={(verification: { name: string; bizNumber: string }) => {
                setValue(`document.participantMappingsList.${idx}.verification.${type}`, { ...verification, used: true });
                setAbstractModalZ2({ isOpen: false });
              }}
            />
          )}
        </>
      ),
    });
  };

  const openExternalReferenceModal = () => {
    const onSubmit = async (carbonCopies: CarbonCopies[]) => {
      setValue("document.carbonCopiesList", carbonCopies);
      setAbstractModalZ2({ isOpen: false });
    };

    setAbstractModalZ2({
      isOpen: true,
      size: "medium",
      className: "dialog-modal",
      children: (
        <ModuSignExternalReference
          control={control} //
          onClose={() => setAbstractModalZ2({ isOpen: false })}
          onSubmit={(carbonCopies: CarbonCopies[]) => {
            onSubmit(carbonCopies);
          }}
        />
      ),
    });
  };

  return (
    <div className="pa30">
      <article className="">
        <div className="contents-container__sub-title">
          <h2>문서 설정</h2>
        </div>
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p className="required">문서명</p>
          </div>
          <div className="contents-container__grid-contents">
            <Controller
              control={control}
              name="document.title"
              rules={{
                required: "문서명을 입력해주세요.",
                minLength: {
                  value: 2,
                  message: "2자 이상 100자 이내로 입력해 주세요.",
                },
                maxLength: {
                  value: 100,
                  message: "2자 이상 100자 이내로 입력해 주세요.",
                },
                pattern: {
                  value: /^[^/\\:*?"<>|]*$/,
                  message: '/ \\ : * ? " < > | 는 입력이 제한됩니다.',
                },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <BaseInput value={value} onChange={onChange} errorText={error?.message} />
              )}
            />
          </div>
        </section>
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p className="required">서명 유효기간</p>
          </div>
          <div className="contents-container__grid-contents">
            <div className="minmax240">
              <Controller
                control={control}
                name="document.participantMappingsList.0.signingDuration"
                rules={{
                  required: {
                    value: true,
                    message: "필수입력 항목입니다.",
                  },
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <BaseDatePicker
                    setDate={(date: Date) => {
                      const duration = moment(date).format(YmdFormat.WITH_TIME_ZONE);
                      setValue(
                        "document.participantMappingsList",
                        watch("document.participantMappingsList").map((participant: DocumentParticipantMapping) => ({
                          ...participant,
                          signingDuration: duration,
                        })),
                      );
                    }}
                    placeholder="기간 선택"
                    selectedDate={value ? moment(value).toDate() : null}
                    minDate={moment().toDate()}
                    errorText={error?.message}
                  />
                )}
              />
            </div>
          </div>
        </section>
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p>완료문서(.pdf) 비밀번호</p>
          </div>
          <div className="contents-container__grid-contents">
            <div className="">
              <div className="flex-center">
                <BaseCheckbox
                  id="moduSignStepTwoPassword"
                  name="moduSignStepTwoPassword"
                  checked={isPasswordUsed || !!watch("document.fileOpenPassword")}
                  label="설정"
                  onChange={setIsPasswordUsed}
                />
                {(isPasswordUsed || !!watch("document.fileOpenPassword")) && (
                  <Controller
                    control={control}
                    name="document.fileOpenPassword"
                    rules={{
                      validate: (value) => {
                        if (!value) return true; // 빈값은 허용

                        // 영문, 숫자, 특수문자 포함 6~32자
                        const passwordRegex = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]).{6,32}$/;
                        return passwordRegex.test(value) || "영문, 숫자, 특수문자 포함 6~32자 이내로 입력해 주세요.";
                      },
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <div className="flex-direction-column">
                        <BaseInput
                          onIconClick={() => {
                            setPasswordHidden(!passwordHidden);
                          }}
                          rightIconClassName={passwordHidden ? "ic-eyes-close" : "ic-eyes-open"}
                          className={"minmax400 ml10 ic-eyes-open"}
                          placeholder="영문, 숫자, 특수문자 포함 6~32자 이내로 입력해주세요"
                          type={passwordHidden ? "password" : "text"}
                          value={value}
                          onChange={onChange}
                          // errorText={error?.message}
                        />
                        {error?.message && <span className="validation-text position-absolute">{error?.message}</span>}
                      </div>
                    )}
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      </article>

      <article className="">
        <div className="contents-container__sub-title pt20 pb10">
          <h2>받는 사람</h2>
        </div>

        {watch("document.participantMappingsList").map((participant: DocumentParticipantMapping, idx: number) => (
          <div key={participant.role + idx} className="my10">
            <div className="my20">
              <span className="font-weight-medium font14">
                {idx + 1}번째 서명자 - {participant.role}
              </span>
            </div>
            <div className="flex-center">
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">이름</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax200">
                    <Controller
                      control={control}
                      name={`document.participantMappingsList.${idx}.name`}
                      rules={{
                        required: {
                          value: true,
                          message: "필수입력 항목입니다.",
                        },
                      }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <BaseInput placeholder="이름을 입력해주세요" value={value} onChange={onChange} errorText={error?.message} />
                      )}
                    />
                  </div>
                </div>
              </section>

              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>인증 방법</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div>
                    <div className="flex-center">
                      {verificationOptions.map((item) => {
                        // 현재 참여자의 인증 정보
                        const verification = participant.verification;
                        let isDisabled = false;

                        // 휴대폰과 법인인증서는 상호배타적
                        if (item.value === "mobileIdentification") {
                          isDisabled = verification?.dCert?.used === true;
                        } else if (item.value === "dCert") {
                          isDisabled = verification?.mobileIdentification?.used === true;
                        }

                        return (
                          <BaseChipWithBtn
                            title={item.title}
                            className="mr10"
                            key={item.value}
                            disabled={isDisabled}
                            // disabled={true}
                            selected={verification?.[item.value as keyof DocumentVerification]?.used}
                            onClick={() => {
                              openVerificationModal(item.value as VerificationType, idx);
                            }}
                            onClear={() => {
                              // 현재 verification 객체 가져오기
                              const currentVerification = getValues(`document.participantMappingsList.${idx}.verification`) || {};
                              // 해당 타입 필드 삭제
                              const { [item.value as keyof DocumentVerification]: removed, ...leftVerification } = currentVerification;

                              // 삭제된 verification으로 업데이트
                              setValue(`document.participantMappingsList.${idx}.verification`, leftVerification);
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>
            </div>
            <div className="flex-center">
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">서명 요청 방법</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="">
                    <div className="flex-center">
                      <Controller
                        control={control}
                        name={`document.participantMappingsList.${idx}.signingMethod.type`}
                        rules={{
                          required: {
                            value: true,
                            message: "필수선택 항목입니다.",
                          },
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <BaseSelect
                            className="minmax100 mr10"
                            menuPosition="fixed"
                            stateOptions={SigningMethodOptions}
                            value={value}
                            setStateValue={onChange}
                            errorText={error?.message}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name={`document.participantMappingsList.${idx}.signingMethod.value`}
                        rules={{
                          required: {
                            value: true,
                            message: "필수입력 항목입니다.",
                          },
                          validate: (value) => {
                            if (getValues(`document.participantMappingsList.${idx}.signingMethod.type`) === "EMAIL") {
                              return validateEmail(value) || "올바른 이메일 형식이 아닙니다.";
                            } else if (getValues(`document.participantMappingsList.${idx}.signingMethod.type`) === "KAKAO") {
                              return isValidKoreanMobile(value) || INVALID_PHONE_NUMBER_MESSAGE;
                            }
                            return true;
                          },
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <BaseInput
                            placeholder={
                              getValues(`document.participantMappingsList.${idx}.signingMethod.type`) === "EMAIL"
                                ? "이메일을 입력해주세요"
                                : "휴대폰 번호를 입력해주세요"
                            }
                            className="minmax180"
                            onChange={(text: string) => {
                              if (getValues(`document.participantMappingsList.${idx}.signingMethod.type`) === "EMAIL") {
                                onChange(text.trim());
                              } else {
                                onChange(formatPhoneNumber(text.trim()));
                              }
                            }}
                            value={value}
                          />
                        )}
                      />
                    </div>
                    {errors?.document?.participantMappingsList?.[idx]?.signingMethod?.value && (
                      <span className="validation-text">{errors?.document?.participantMappingsList?.[idx]?.signingMethod?.value?.message}</span>
                    )}
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>언어</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax120">
                    <Controller
                      control={control}
                      name={`document.participantMappingsList.${idx}.locale`}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <BaseSelect stateOptions={LanguageOptions} setStateValue={onChange} value={value} errorText={error?.message} />
                      )}
                    />
                  </div>
                </div>
              </section>
            </div>
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>전달 메세지</p>
              </div>
              <div className="contents-container__grid-contents">
                <div>
                  <Controller
                    control={control}
                    name={`document.participantMappingsList.${idx}.requesterMessage`}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <BaseInput placeholder="전달하고 싶은 내용을 입력해주세요" value={value} onChange={onChange} errorText={error?.message} />
                    )}
                  />
                </div>
              </div>
            </section>
          </div>
        ))}
      </article>
      <article>
        <div className="contents-container__sub-title pt20 pb10">
          <h2>외부 참조자</h2>
        </div>
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p>외부 참조자</p>
          </div>
          <div className="contents-container__grid-contents">
            <div>
              <div className="flex-center">
                <BaseButton title="선택" className="color-white" onClick={openExternalReferenceModal} />
                {watch("document.carbonCopiesList.0.contact") && (
                  <p className="ml10">
                    {`${watch("document.carbonCopiesList.0.contact")}`}
                    {watch("document.carbonCopiesList")!.length > 1 && " 외 " + (watch("document.carbonCopiesList")!.length - 1) + "명"}
                    에게 알림 발송
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      </article>
    </div>
  );
};

export default ModuSignStep2;
